/**
 * Apply large UPS list edits from a Request Detail edit session on Save Changes (#996 Phase 2).
 * Mirrors smallUpsEditSessionFlush. Note PUT (not PATCH) for unit updates, and the
 * reorder body key is `largeUpsIds`.
 */

import { api } from './api';
import {
  computeRepeatableOperations,
  type ComputedRepeatableOps,
} from './computeRepeatableOperations';
import { mirrorLargeUpsToOfflineRequestCache } from './largeUpsOfflineSync';
import { toCreateLargeUpsPayload } from './largeUpsPayload';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import { flushRepeatableInstanceAttachmentSectionRemaps } from './repeatableInstanceAttachmentRemapFlush';
import { deleteRepeatableUnitTolerating404 } from './repeatableUnitDelete';
import {
  LEGACY_LARGE_UPS_SYNTHETIC_ID,
  validateSessionLargeUpsNames,
} from '../components/largeups/largeUpsListUtils';
import { LARGE_UPS_REPEATABLE_ATTACHMENT_BASE_SECTIONS } from '../utils/repeatableInstanceAttachmentSections';

import type { LargeUpsConfiguration } from '../types/largeUps';

function isPersistedLargeUpsId(id: number | undefined): id is number {
  return id != null && id > 0;
}

function isSessionOnlyLargeUpsId(id: number | undefined): boolean {
  return id == null || id <= 0 || id === LEGACY_LARGE_UPS_SYNTHETIC_ID;
}

/**
 * Canonicalize for content comparison: sort keys and drop undefined values so
 * two rows with identical CONTENT compare equal regardless of key insertion
 * order. Load-bearing since #997 Story 6.5 (mirror of `smallUpsEditSessionFlush`):
 * the engine save bridge's baseline (raw API/mapper rows, identity keys first)
 * and the session rows (round-tripped through `descriptorInstanceToLargeUps`,
 * identity keys appended last) are different object lineages — a raw
 * `JSON.stringify` flagged every unit as changed, PUTting the whole list on save.
 */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }
  if (value && typeof value === 'object') {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const entry = (value as Record<string, unknown>)[key];
      if (entry !== undefined) {
        sorted[key] = canonicalize(entry);
      }
    }
    return sorted;
  }
  return value;
}

function largeUpsContentKey(unit: LargeUpsConfiguration): string {
  // `requestId` excluded too (#997 Story 6.5, mirror small UPS/rack review): the
  // engine conversion stamps `parseInt('o-…') → 0` on offline-request session
  // rows, which would keep every untouched unit permanently "changed" against
  // the cached baseline.
  const {
    id: _id,
    requestId: _rid,
    createdAt: _ca,
    updatedAt: _ua,
    ...rest
  } = unit;
  return JSON.stringify(canonicalize(rest));
}

function largeUpsEqual(
  a: LargeUpsConfiguration[],
  b: LargeUpsConfiguration[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every(
    (unit, index) => largeUpsContentKey(unit) === largeUpsContentKey(b[index]),
  );
}

export function largeUpsEditSessionHasChanges(
  baseline: LargeUpsConfiguration[],
  session: LargeUpsConfiguration[],
): boolean {
  return !largeUpsEqual(baseline, session);
}

/**
 * Pure save-time diff for large UPS units (#1501): consumed by this flush AND
 * by the aggregate save payload builder — one diff implementation, two
 * consumers.
 */
export function computeLargeUpsOperations(
  baseline: LargeUpsConfiguration[],
  session: LargeUpsConfiguration[],
): ComputedRepeatableOps<LargeUpsConfiguration> {
  return computeRepeatableOperations(baseline, session, {
    isPersistedId: isPersistedLargeUpsId,
    isSessionOnlyId: isSessionOnlyLargeUpsId,
    contentKey: largeUpsContentKey,
    stripForUpdate: unit => {
      const {
        id: _id,
        createdAt: _ca,
        updatedAt: _ua,
        requestId: _rid,
        ...data
      } = unit;
      return data;
    },
  });
}

export async function flushLargeUpsEditSession(
  requestId: string,
  baseline: LargeUpsConfiguration[],
  session: LargeUpsConfiguration[],
  isOfflineRequest: boolean,
): Promise<LargeUpsConfiguration[]> {
  if (!largeUpsEditSessionHasChanges(baseline, session)) {
    return session;
  }

  const nameError = validateSessionLargeUpsNames(session);
  if (nameError) {
    throw new Error(nameError);
  }

  const idKey = String(requestId);
  const useOnlineApi = networkService.isConnected() && !isOfflineRequest;

  const ops = computeLargeUpsOperations(baseline, session);

  for (const deletedId of ops.deletes) {
    if (useOnlineApi) {
      await deleteRepeatableUnitTolerating404(
        `/requests/${idKey}/large-ups/${deletedId}`,
      );
    } else {
      await offlineCache.addLargeUpsToOfflineQueue(
        idKey,
        'delete',
        undefined,
        deletedId,
      );
    }
  }

  const createdIdMap = new Map<number, number>();

  for (const { tempId, row } of ops.creates) {
    const payload = toCreateLargeUpsPayload({
      ...row,
      requestId: parseInt(idKey, 10) || row.requestId,
    });
    if (useOnlineApi) {
      const created = await api.post<LargeUpsConfiguration>(
        `/requests/${idKey}/large-ups`,
        payload,
      );
      if (tempId != null && created.id) {
        createdIdMap.set(tempId, created.id);
      }
    } else {
      await offlineCache.addLargeUpsToOfflineQueue(
        idKey,
        'create',
        payload,
        tempId,
      );
    }
  }

  for (const { id, data } of ops.updates) {
    if (useOnlineApi) {
      await api.put<LargeUpsConfiguration>(
        `/requests/${idKey}/large-ups/${id}`,
        data,
      );
    } else {
      await offlineCache.addLargeUpsToOfflineQueue(idKey, 'update', data, id);
    }
  }

  const tempIdByClientKey = new Map(
    ops.creates.map(c => [c.clientKey, c.tempId]),
  );
  const orderedPersistedIds = ops.orderRefs
    .map(ref => {
      if (typeof ref === 'number') {
        return ref;
      }
      const tempId = tempIdByClientKey.get(ref);
      return tempId != null && createdIdMap.has(tempId)
        ? createdIdMap.get(tempId)!
        : null;
    })
    .filter((id): id is number => id != null);

  if (
    orderedPersistedIds.length > 0 &&
    JSON.stringify(orderedPersistedIds) !==
      JSON.stringify(ops.baselineOrderInSession)
  ) {
    if (useOnlineApi) {
      await api.patch<LargeUpsConfiguration[]>(
        `/requests/${idKey}/large-ups/reorder`,
        { largeUpsIds: orderedPersistedIds },
      );
    } else {
      await offlineCache.addLargeUpsToOfflineQueue(
        idKey,
        'reorder',
        undefined,
        undefined,
        orderedPersistedIds,
      );
    }
  }

  await flushRepeatableInstanceAttachmentSectionRemaps(
    idKey,
    createdIdMap,
    LARGE_UPS_REPEATABLE_ATTACHMENT_BASE_SECTIONS,
    useOnlineApi,
  );

  const normalizedSession = session.map(unit => {
    if (unit.id != null && createdIdMap.has(unit.id)) {
      return { ...unit, id: createdIdMap.get(unit.id)! };
    }
    return unit;
  });

  if (!useOnlineApi) {
    await offlineCache.updateCachedRequestLargeUps(idKey, normalizedSession);
  } else {
    await mirrorLargeUpsToOfflineRequestCache(idKey, normalizedSession);
  }

  return normalizedSession;
}
